import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Switch, useParams, useHistory } from 'react-router';
import * as routes from 'routes';
import { Provider, inject, observer } from 'mobx-react';

import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Link from '@material-ui/core/Link';

import { Permission } from 'helpers/permissions';
import compose from 'helpers/compose';
import Layout from 'components/Layout/Layout';
import CrumbRoute from 'components/CrumbRoute';
import Background from 'components/Layout/Background';

import AccountDetail from './AccountDetail';
import { Params } from '../Edit/accountEditRoutes';
import { RootStore } from 'stores/rootStore';
import { Client } from 'enums/index';

interface Props {
  rootStore: RootStore;
}

const Index: React.FC<Props> = ({ rootStore }) => {
  const userMode = rootStore.stores.userStore.checkPermissions(Permission.APP_ENDUSER);
  const { t } = useTranslation();
  const history = useHistory();
  const { userStore } = rootStore.stores;
  const accountIdParam = useParams<Params>().accountId;
  const [accountId, setAccountId] = useState(userMode ? userStore.activeAccount?.accountId : accountIdParam);

  useEffect(() => {
    const id = history.location.pathname.split('/')[1];
    const vsParam = userMode ? userStore.validateVs(id) : undefined;
    const idParam = userMode ? userStore.validateAccountId(id) : undefined;
    userStore.accountIdFromVs(vsParam, idParam);
  }, [history.location.pathname, userStore, userMode]);

  useEffect(() => {
    setAccountId(userMode ? userStore.activeAccount?.accountId : accountIdParam);
  }, [userMode, userStore.activeAccount?.accountId, accountIdParam]);

  // FIXME: account switching?
  /*
  const accountId = useMemo(
    () => (userMode ? userStore.activeAccount?.accountId : accountIdParam),
    [userMode, userStore.activeAccount?.accountId, accountIdParam],
  );
  */
  //const accountId = userMode ? userStore.activeAccount?.accountId : accountIdParam;

  const { apiStore } = rootStore.stores;

  if (
    userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN &&
    userStore?.clientData?.existingClient &&
    !userStore?.clientData?.signatureAccess
  ) {
    return (
      <Dialog open={true} maxWidth="sm">
        <DialogContent>
          <Typography style={{ textAlign: 'center' }}>
            {t('accountDetail_existingClient_domain')}
            <Link
              style={{ cursor: 'pointer' }}
              onClick={() => userStore.logout(window.location.href.replace('podpis', 'klient'))}
            >
              {t('accountDetail_existingClient_login')}
            </Link>
            {t('accountDetail_existingClient_proceed')}
          </Typography>
        </DialogContent>
      </Dialog>
    );
  }

  if (userMode && userStore.clientData?.accounts?.length === 0 && !userStore?.clientData?.signatureAccess) {
    return (
      <Layout detail key={accountId} title={t('pageTitle_noAccounts')}>
        <Background>
          <Typography variant="h1" style={{ textAlign: 'center' }}>
            {t('accountDetail_noAccounts')}
          </Typography>
        </Background>
      </Layout>
    );
  }

  //if (userMode && !userStore.activeAccount && userStore?.clientData?.signatureAccess) return null;

  return accountId ? ( // account detail - view | signature
    <Layout
      detail
      key={accountId}
      title={t('pageTitle_accountDetail')}
      signatureOnly={userStore.loggedUser?.client === Client.CLIENT_PORTAL_SIGN}
    >
      <Provider apiStore={apiStore}>
        {userMode ? (
          <AccountDetail />
        ) : (
          <Switch>
            <CrumbRoute
              exact
              title={[t('navMenu_accounts'), `${accountId}`, t('accounts_detail')]}
              path={[
                routes.ACCOUNTS_DETAIL(),
                routes.ACCOUNTS_DETAIL_HISTORY(),
                routes.ACCOUNTS_DETAIL_REPORTS(),
                routes.ACCOUNTS_DETAIL_ID(),
                routes.ACCOUNTS_DETAIL_PLAN(),
                routes.ACCOUNTS_DETAIL_DOCUMENTS(),
                routes.ACCOUNTS_DETAIL_FEES(),
                routes.ACCOUNTS_DETAIL_MOVEMENTS(),
                routes.ACCOUNTS_DETAIL_PERSONAL(),
                routes.ACCOUNTS_DETAIL_WITHDRAWAL(),
                routes.ACCOUNTS_DETAIL_TRANSFER(),
              ]}
              component={AccountDetail}
              home={routes.ACCOUNTS_ACTIVE}
            />
          </Switch>
        )}
      </Provider>
    </Layout>
  ) : userMode && userStore?.clientData?.signatureAccess ? ( // no particular account/accountId; general requests
    <Layout detail signatureOnly title={t('pageTitle_accountDetail')}>
      <Provider apiStore={apiStore}>
        <AccountDetail signatureAccess />
      </Provider>
    </Layout>
  ) : null;
};

export default compose(inject('rootStore'), observer)(Index);
